import React from "react";
import { useDrop } from "react-dnd";
import { FormBuilderRowView } from "../row/FormBuilderRowView";
import { cloneDeep } from "lodash";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { useFormBuilderEditorContext } from "../../../../formBuilderEditorContext";
import { formBuilderGenerateFormDesignStyles } from "../../../../model/formBuilderForm";
import { formBuilderGenerateDefaultRow } from "../../../../model/formBuilderRow";
import { TFormBuilderItem } from "../../../../model/item/formBuilderItem";
import { FormBuilderOverlay } from "../../../overlay/FormBuilderOverlay";
import { useAppSelector } from "hooks/redux";

interface IFormBuilderFormViewDropState {
  isOver: boolean;
}

export const ACCEPTED_FORM_BUILDER_DROP_ITEM = "form-builder-drop-item";

export function FormBuilderFormView() {
  const { formBuilderController, formRef } = useFormBuilderEditorContext();

  const { t } = useTranslation();

  const rootCssVariables = useAppSelector(store => store.websiteBuilderStore.editor.rootCssVariables);

  const designStyles = formBuilderGenerateFormDesignStyles();

  const [{ isOver }, drop] = useDrop<TFormBuilderItem, unknown, IFormBuilderFormViewDropState>(
    () => ({
      accept: ACCEPTED_FORM_BUILDER_DROP_ITEM,
      canDrop: item => item != null,
      drop: item => {
        if (formBuilderController.form.children.length === 0) {
          const entity = formBuilderGenerateDefaultRow([item]);
      
          formBuilderController.addEntity({
            snapshot: {
              type: "row",
              entity,
              entityIndex: 0,
            },
          });
        } else if (formBuilderController.overlayTarget != null) {
          const { type, key, anchor } = formBuilderController.overlayTarget;

          if (type === "row") {
            const targetRowIndex = formBuilderController.form.children.findIndex(row => row.key === key);

            if (targetRowIndex === -1) {
              return;
            }

            const entity = formBuilderGenerateDefaultRow([item]);
            const entityIndex = anchor === "above" ? targetRowIndex : targetRowIndex + 1;

            formBuilderController.addEntity({
              snapshot: {
                type: "row",
                entity,
                entityIndex,
              },
            });
          } else if (type === "item") {
            const parentRowIndex = formBuilderController.form.children.findIndex(row => row.children.some(child => child.key === key));
            const parentRow = formBuilderController.form.children[parentRowIndex];

            if (parentRowIndex === -1 || parentRow == null) {
              return;
            }

            const targetItemIndex = parentRow.children.findIndex(child => child.key === key);

            const entityIndex = anchor === "left" ? targetItemIndex : targetItemIndex + 1;

            formBuilderController.addEntity({
              snapshot: {
                type: "item",
                origin: "ADD_ENTITY",
                entity: item,
                entityIndex,
                parentRow: cloneDeep(parentRow),
                parentRowIndex,
              },
            });
          }
        }
      },
      collect: monitor => ({
        isOver: monitor.isOver(),
      }),
    }),
    [formBuilderController],
  );

  return (
    <div
      ref={el => {
        drop(el);
        formRef.current = el;
      }}
      className={classNames(designStyles.className, "builder-form")}
      style={{
        ...designStyles.styleProperties,
        ...rootCssVariables,
      }}
    >
      {formBuilderController.form.children.length > 0 ? (
        formBuilderController.form.children.map(row => (
          <FormBuilderRowView
            key={row.key}
            row={row}
          />
        ))) : (
          <div
            className={classNames("builder-form-empty-drop-message", {
              "empty-drop-message-drag-over": isOver,
            })}
          >
            <h3>{t("Drop items onto your Form here")}</h3>
            <p>{t("Start by dragging form items from the left panel into this space.")}</p>
          </div>
        )
      }

      <FormBuilderOverlay isOver={isOver} />
    </div>
  );
}